import React, {useContext} from 'react';
import PropTypes from 'prop-types';

import {Button, Dropdown, DropdownItem, Text, Toggle} from '@albert-io/atomic';
import PracticeViewToolbar from 'components/PracticeView/PracticeViewToolbar/PracticeViewToolbar.react';
import useDispatchMethods from 'lib/hooks/useDispatchMethods';
import useUpdateOnInterval from 'lib/hooks/useUpdateOnInterval';

import {getSecondsLeftInTimeLimit, isTimerUndismissable} from '../TimeManager/timeManager.utils';
import TimeManager from '../TimeManager/TimeManager.react';
import SubmitAssignmentButton from '../SubmitAssignmentButton/SubmitAssignmentButton.react';
import ToolbarProgressIndicator from '../ToolbarProgressIndicator/ToolbarProgressIndicator.react';
import {AssignmentContext} from '../assignmentPracticeView.utils';

function makeToggleStateProperty(property) {
  return ({state}) => ({
    ...state,
    [property]: !state[property]
  });
}

const dispatchMethods = {
  toggleIsViewMoreOpen: makeToggleStateProperty('isViewMoreOpen'),
  toggleShowProgress: makeToggleStateProperty('showProgress'),
  toggleShowAccuracy: makeToggleStateProperty('showAccuracy'),
  toggleShowTimeRemaining: makeToggleStateProperty('showTimeRemaining')
};

const initialState = {
  isViewMoreOpen: false,
  showProgress: true,
  showAccuracy: true,
  showTimeRemaining: true
};

export default function InProgressToolbar({
  invalidateAssignment,
  answeredQuestionCount,
  questionCount,
  accuracy = null
}) {
  useUpdateOnInterval();
  const {assignment, classroomId, studentId} = useContext(AssignmentContext);
  const secondsLeft = getSecondsLeftInTimeLimit({assignment, classroomId, studentId});
  const [state, dispatch] = useDispatchMethods(dispatchMethods, initialState);
  const isTimed = assignment.getTimeLimitFromSettings({classroomId, studentId}) !== null;
  const canDismissTimer = isTimed && !isTimerUndismissable(secondsLeft);
  return (
    <PracticeViewToolbar align='start'>
      <SubmitAssignmentButton
        as={Button}
        size='s'
        onEnd={invalidateAssignment}
        className='u-mar-r_1'
      >
        <Text bold size='s' color='white'>
          Submit assignment
        </Text>
      </SubmitAssignmentButton>
      <Dropdown
        trigger={
          <Button size='s' label='Toggle more options' className='u-mar-r_1'>
            <Text bold size='s' color='white'>
              Options
            </Text>
          </Button>
        }
      >
        {isTimed && (
          <ToggleableItem
            label='Show time remaining'
            checked={!canDismissTimer || state.showTimeRemaining}
            onChange={dispatch.toggleShowTimeRemaining}
            disabled={!canDismissTimer}
          />
        )}
        <ToggleableItem
          label='Show progress'
          checked={state.showProgress}
          onChange={dispatch.toggleShowProgress}
        />
        {accuracy !== null && (
          <ToggleableItem
            label='Show accuracy'
            checked={state.showAccuracy}
            onChange={dispatch.toggleShowAccuracy}
          />
        )}
      </Dropdown>
      {isTimed && (state.showTimeRemaining || !canDismissTimer) && (
        <TimeManager secondsLeft={secondsLeft} onOutOfTime={invalidateAssignment} />
      )}
      {state.showProgress && (
        <ToolbarProgressIndicator
          answeredQuestionCount={answeredQuestionCount}
          totalQuestionCount={questionCount}
        />
      )}
      {accuracy !== null && state.showAccuracy && (
        <Text size='s' bold className='u-mar-r_1 u-pad-lr_1'>
          Accuracy: {accuracy}%
        </Text>
      )}
    </PracticeViewToolbar>
  );
}

InProgressToolbar.propTypes = {
  invalidateAssignment: PropTypes.func.isRequired,
  answeredQuestionCount: PropTypes.number.isRequired,
  questionCount: PropTypes.number.isRequired,
  accuracy: PropTypes.number
};

function ToggleableItem({label, checked, onChange, disabled, ...rest}) {
  return (
    <DropdownItem onClick={(e) => e.stopPropagation()} {...rest}>
      <div className='u-display_flex u-justify-content_space-between u-align-items_center'>
        <Text bold emphasis='medium' className='u-white-space_nowrap'>
          {label}
        </Text>
        <Toggle className='u-mar-l_2' checked={checked} onChange={onChange} disabled={disabled} />
      </div>
    </DropdownItem>
  );
}

ToggleableItem.propTypes = {
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};
